export interface Category {
  id: number; // BIGINT from Supabase (serial/bigserial)
  name: string;
  description?: string;
  type: CategoryType;
  color?: string;
  user_id: string;
  created_at: string; // ISO string from Supabase
  updated_at: string; // ISO string from Supabase
  status?: CategoryStatus;
  is_active?: boolean;
  usage_count?: number;
}

export enum CategoryType {
  INCOME = 'income',
  EXPENSE = 'expense'
}

export enum CategoryStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

export interface CategoryFormData {
  name: string;
  description?: string;
  type: CategoryType;
  color?: string;
}

export interface CategoryMutationResult {
  data: Category | null;
  error: string | null;
  loading: boolean;
}

export interface CategorySummary {
  total: number;
  by_type: {
    [CategoryType.INCOME]: number;
    [CategoryType.EXPENSE]: number;
  };
  by_status: {
    [CategoryStatus.ACTIVE]: number;
    [CategoryStatus.INACTIVE]: number;
  };
  most_used: Category[];
  least_used: Category[];
}

// Utility functions
export const formatCategoryType = (type: CategoryType): string => {
  switch (type) {
    case CategoryType.INCOME:
      return 'Receita';
    case CategoryType.EXPENSE:
      return 'Despesa';
    default:
      return 'Desconhecido';
  }
};

export const formatCategoryStatus = (status: CategoryStatus): string => {
  switch (status) {
    case CategoryStatus.ACTIVE:
      return 'Ativo';
    case CategoryStatus.INACTIVE:
      return 'Inativo';
    default:
      return 'Desconhecido';
  }
};

export const getCategoryStatusColor = (status: CategoryStatus): string => {
  switch (status) {
    case CategoryStatus.ACTIVE:
      return 'text-green-600 bg-green-50 border-green-200';
    case CategoryStatus.INACTIVE:
      return 'text-gray-600 bg-gray-50 border-gray-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

export const getCategoryTypeColor = (type: CategoryType): string => {
  switch (type) {
    case CategoryType.INCOME:
      return 'text-green-600 bg-green-50 border-green-200';
    case CategoryType.EXPENSE:
      return 'text-red-600 bg-red-50 border-red-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

export const validateCategoryName = (name: string): boolean => {
  return name.trim().length >= 2 && name.trim().length <= 50;
};

export const buildCategorySummary = (categories: Category[]): CategorySummary => {
  const summary: CategorySummary = {
    total: categories.length,
    by_type: {
      [CategoryType.INCOME]: 0,
      [CategoryType.EXPENSE]: 0
    },
    by_status: {
      [CategoryStatus.ACTIVE]: 0,
      [CategoryStatus.INACTIVE]: 0
    },
    most_used: [],
    least_used: []
  };

  categories.forEach(category => {
    summary.by_type[category.type]++;
    summary.by_status[category.status]++;
  });

  // Sort by usage and get most/least used
  const sortedByUsage = [...categories].sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0));
  summary.most_used = sortedByUsage.slice(0, 5);
  summary.least_used = sortedByUsage.slice(-5).reverse();

  return summary;
};

// Default category structure interface for seeding service
export interface DefaultCategory {
  name: string;
  type: CategoryType;
  color: string;
  description?: string;
}